import { ScopedCssBaseline, Typography } from "@mui/material";
import type { SxProps } from "@mui/material";
import type { DataGridProps } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import styled, { useTheme } from "styled-components";

import { useMUIDataGridLocale } from "./useMUIDataGridLocale";

interface DetermineHideFooterArgs {
  rowCount?: number;
  rowsLength: number;
}

function determineHideFooter({ rowCount, rowsLength }: DetermineHideFooterArgs): boolean {
  if (rowCount === undefined) {
    return rowsLength < 10;
  }

  return rowCount < 10;
}

interface Props extends Omit<DataGridProps, "sx"> {
  title?: string;
  notifications?: number | string;
  transparentBackground?: boolean;
  sx?: SxProps;
  toolbarContent?: React.ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
}

export const MUIDataGrid: React.FC<Props> = ({
  title,
  notifications,
  localeText,
  transparentBackground = false,
  sx: customSx,
  toolbarContent,
  hideHeader = false,
  hideFooter: forceHideFooter = false,
  ...props
}) => {
  const locale = useMUIDataGridLocale();
  const theme = useTheme();

  const getDataGrid = () => {
    const isEmpty = props.rows?.length === 0;
    let gridHeight: number | "auto" = isEmpty ? 240 : "auto";
    if (isEmpty && props.loading) {
      gridHeight = 420;
    }

    return (
      <DataGrid
        pageSizeOptions={[5, 10, 25, 50]}
        sx={{
          border: 0,
          backgroundColor: "#FFF",
          cursor: "pointer",
          height: gridHeight,
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: `1px solid ${theme.colors.redesign.db30}`,
          },
          ".MuiDataGrid-columnHeader": {
            padding: 0,
            borderBottom: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "16px 10px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: theme.colors.redesign.db70,
            fontSize: "14px",
          },
          "& .MuiDataGrid-columnHeader[data-field='__check__'] .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "0",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnSeparator": {
            color: theme.colors.redesign.db30,
          },
          "& .MuiCheckbox-root": {
            "& svg": {
              color: theme.colors.redesign.db40,
            },
            "&.Mui-checked svg": {
              color: theme.colors.redesign.b100,
            },
          },
          "& .MuiDataGrid-cell": {
            borderTop: "none",
            borderBottom: `1px solid ${theme.colors.redesign.db20}`,
            background: theme.colors.white,
            color: theme.colors.redesign.db100,
            display: "flex",
            alignItems: "center",
            fontSize: "14px",
            transition: "background-color .1s",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
            backgroundColor: theme.colors.redesign.b5,
          },
          "& .MuiDataGrid-virtualScroller": {
            paddingBottom: "1px",
          },
          "& .MuiDataGrid-filler, & .MuiDataGrid-cellEmpty, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop":
            {
              display: "none",
            },
          "& .MuiDataGrid-footerContainer": {
            background: "transparent",
            minHeight: 56,
            border: "none",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            color: theme.colors.redesign.db100,
            fontSize: 14,
            fontWeight: 500,
            margin: 0,
          },
          "& .MuiTablePagination-select": {
            color: theme.colors.redesign.db100,
            fontSize: 14,
            padding: 0,
            margin: "7px 16px",
          },
          "& .MuiTablePagination-selectIcon": {
            color: theme.colors.redesign.db100,
          },
          "& .MuiTablePagination-toolbar .MuiInputBase-root": {
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.redesign.db30}`,
            borderRadius: 1,
          },
          "& .MuiTablePagination-actions .MuiIconButton-root": {
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.redesign.db30}`,
            borderRadius: 1,
            width: 36,
            height: 36,
            marginLeft: 1,
            "& svg": {
              transition: ".15s",
            },
            "&.Mui-disabled": {
              backgroundColor: "transparent",
            },
            "&:hover": {
              backgroundColor: theme.colors.redesign.b10,
              color: theme.colors.redesign.b100,
            },
            "&:active": {
              borderColor: theme.colors.redesign.b100,
            },
          },
          "& .MuiDataGrid-booleanCell[data-value='true']": {
            color: theme.colors.redesign.g100,
          },
          "& .MuiDataGrid-booleanCell[data-value='false']": {
            color: theme.colors.redesign.r100,
          },
          "& .MuiSkeleton-root": {
            backgroundColor: theme.colors.redesign.b10,
            "&::after": {
              background: `linear-gradient(90deg, transparent, ${theme.colors.redesign.b20}, transparent)`,
            },
          },
          ...customSx,
        }}
        localeText={{ ...locale.components.MuiDataGrid.defaultProps.localeText, ...localeText }}
        hideFooter={
          forceHideFooter
            ? true
            : determineHideFooter({ rowCount: props.rowCount, rowsLength: props.rows?.length || 0 })
        }
        logLevel={false}
        {...props}
        slotProps={{
          ...props.slotProps,
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
            ...props.slotProps?.loadingOverlay,
          },
        }}
      />
    );
  };

  return (
    <ScopedCssBaseline sx={{ backgroundColor: "transparent" }}>
      <Container $transparentBackground={transparentBackground}>
        {!hideHeader && (
          <TableHeader>
            <HeaderTitle>
              <Typography variant="h3" component="span" sx={{ display: "inline" }}>
                {title}
              </Typography>
              {(typeof notifications === "number" || (typeof notifications === "string" && notifications)) && (
                <Notifications>{notifications}</Notifications>
              )}
            </HeaderTitle>
            {toolbarContent}
          </TableHeader>
        )}
        <TableContainer>{getDataGrid()}</TableContainer>
      </Container>
    </ScopedCssBaseline>
  );
};

export const Container = styled.div<{ $transparentBackground?: boolean }>`
  background: ${({ theme, $transparentBackground }) =>
    $transparentBackground ? "transparent" : theme.semantic.container.background};
  border-radius: 12px;
  padding: ${({ $transparentBackground }) => ($transparentBackground ? "0" : "24px")};
`;

export const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const Notifications = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 16px;
  white-space: nowrap;
  ${props => ({ ...props.theme.font.link3 })};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.redesign.b10};
  padding: 4px 8px;
  color: ${({ theme }) => theme.colors.redesign.b100};
`;

export const Title = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 48px;
  padding-left: 10px;
  color: ${({ theme }) => theme.colors.greys.slate};
`;

const HeaderTitle = styled.div`
  display: inline;
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.semantic.container.background};
  border-radius: 12px;
`;
